import { notificationService } from '@/services/notificationService'
import { type FormEvent, type KeyboardEvent, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import useAccessStore from '@/store/accessStore'
import EnterOTPForm from './EnterOTPForm'
import styles from './forgotPassword.module.css'

interface EnterOTPProps {
  onLinkClick: (page: string) => void
  onSuccess: (email: string) => void
}

export const EnterOTP = ({ onLinkClick, onSuccess }: EnterOTPProps) => {
  const location = useLocation()
  const navigate = useNavigate()

  const isRegistrationOtp = location.pathname.includes('/registration/enter-otp')
  const isForgotPasswordOtp = location.pathname.includes('/forgot-password/enter-otp')

  const email = useMemo(() => {
    if (location.state?.email) {
      return location.state.email
    }

    try {
      const last = sessionStorage.getItem('lastLoginEmail')
      if (last) return last
    } catch {
      /* ignore */
    }

    return ''
  }, [location.state?.email])

  const [otpValues, setOtpValues] = useState<string[]>(['', '', '', ''])
  const [errors, setErrors] = useState<{ general?: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [generalError, setGeneralError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [, setIsResending] = useState(false)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const { verifyOTP, verifyRegistrationOTP, resetPassword } = useAccessStore()

  const validateForm = (): boolean => {
    const otp = otpValues.join('')
    if (otp.length !== 4) {
      const errorMsg = 'Please enter all 4 digits'
      setGeneralError(errorMsg)
      notificationService.error(errorMsg)
      return false
    }
    if (!/^\d{4}$/.test(otp)) {
      const errorMsg = 'Please enter only numbers'
      setGeneralError(errorMsg)
      notificationService.error(errorMsg)
      return false
    }
    return true
  }

  const handleOTPChange = (index: number, value: string) => {
    if (value.length > 1 || (value && !/^\d$/.test(value))) {
      return
    }

    const newOtpValues = [...otpValues]
    newOtpValues[index] = value
    setOtpValues(newOtpValues)

    if (generalError) {
      setGeneralError('')
    }
    if (errors.general) {
      setErrors({})
    }
    if (isSubmitting) {
      setIsSubmitting(false)
    }

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    const otp = otpValues.join('')
    setIsSubmitting(true)

    try {
      setIsLoading(true)

      // Use appropriate verification function based on flow
      const verifyResponse = isRegistrationOtp ? await verifyRegistrationOTP(email, otp) : await verifyOTP(email, otp)

      if (verifyResponse === 200) {
        notificationService.success('Email verified successfully!')

        // Navigate to appropriate page based on flow
        if (isRegistrationOtp) {
          // Registration flow - go to profile
          if (onSuccess) {
            onSuccess(email)
          }
          navigate('/registration/profile', { replace: true })
        } else if (isForgotPasswordOtp) {
          // Forgot password flow - go to create new password
          if (onSuccess) {
            onSuccess(email)
          }
          // onSuccess will trigger navigation to create-new-password via App.tsx
        }
      } else {
        setIsSubmitting(false)
      }
    } catch (error: unknown) {
      console.error('Verify OTP error:', error)
      const responseData = (error as { response?: { data?: Record<string, unknown> } }).response?.data

      if (responseData) {
        const errorMessage = responseData && typeof responseData.message === 'string' ? responseData.message : 'Validation failed.'
        setErrors({
          general: errorMessage,
        })
        setGeneralError(errorMessage)
        notificationService.error(errorMessage)
      } else {
        const fallbackMessage = 'Invalid OTP. Please try again.'
        setErrors({
          general: fallbackMessage,
        })
        setGeneralError(fallbackMessage)
        notificationService.error(fallbackMessage)
      }
    } finally {
      setIsLoading(false)
      setIsSubmitting(false)
    }
  }

  const handleResendOTP = async () => {
    setIsResending(true)
    try {
      const resetPasswordResponse = await resetPassword(email)
      if (resetPasswordResponse === 200) {
        notificationService.success('Successfully sent code to your email!')
      }
    } catch (error: unknown) {
      console.error('Reset password error:', error)
      setErrors({
        general: 'Failed to send code to your email',
      })
      notificationService.error('Failed to resend code')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className={styles.mainContentWrapper}>
      <h1 className={styles.title}>Verify Email</h1>
      <p className={styles.textGrey}>A verification code has been sent to your provided email.</p>

      <EnterOTPForm
        otpValues={otpValues}
        // errors={errors}
        // generalError={generalError}
        isLoading={isLoading}
        inputRefs={inputRefs}
        handleOTPChange={handleOTPChange}
        handleKeyDown={handleKeyDown}
        handleSubmit={handleSubmit}
        onLinkClick={onLinkClick}
        onBack={async () => {
          if (isRegistrationOtp) {
            try {
              const { default: useAccessStore } = await import('@/store/accessStore')
              await useAccessStore.getState().logout()
            } finally {
              onLinkClick('login')
            }
          } else {
            // For forgot password, just go back to login
            onLinkClick('login')
          }
        }}
        isSubmitting={isSubmitting}
        handleResendOTP={handleResendOTP}
      />
    </div>
  )
}
